import { type KeyboardEvent as ReactKeyboardEvent, type ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'wouter'

import { cn } from '@/lib/cn'
import { useNavItems } from '@/hooks'
import { useTheme } from '@/lib/theme'
import { currentLanguage, setLanguage } from '@/lib/language'

import {
  ChevronLeftIcon,
  GlobeIcon,
  MoonIcon,
  RefreshIcon,
  SearchIcon,
  SunIcon,
  type IconProps,
} from './Icons'
import { NAV_ICONS } from './Sidebar'
import styles from './palette.module.css'

/**
 * Command palette (Cmd/Ctrl+K) and the console's keyboard shortcuts.
 *
 * Everything reachable from the sidebar is reachable from here by typing a
 * few letters of its name, in either language, plus the handful of shell
 * actions an operator otherwise has to hunt for in the top bar.
 */

interface Command {
  id: string
  label: string
  /** Extra text matched by the filter but never shown. */
  keywords: string
  group: string
  icon: (props: IconProps) => ReactElement
  shortcut?: readonly string[]
  run: () => void
}

/** Two-key "go to" sequences: press g, then the letter. */
export const GO_SHORTCUTS: Readonly<Record<string, string>> = {
  o: '/',
  i: '/identities',
  p: '/proxies',
  s: '/scheduler',
  k: '/api-keys',
  l: '/logs',
  y: '/system',
  d: '/diagnose',
  t: '/settings',
}

const GO_KEY_FOR_PATH: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(GO_SHORTCUTS).map(([key, path]) => [path, key]),
)

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

function normalise(value: string): string {
  return value.toLocaleLowerCase().normalize('NFKD').replace(/\s+/g, ' ').trim()
}

/** Every query word must appear somewhere; earlier hits rank higher. */
function score(command: Command, query: string): number {
  if (!query) return 1
  const haystack = normalise(`${command.label} ${command.keywords}`)
  let total = 0
  for (const word of query.split(' ')) {
    const at = haystack.indexOf(word)
    if (at < 0) return 0
    total += at === 0 ? 3 : haystack[at - 1] === ' ' ? 2 : 1
  }
  return total
}

export interface ShellShortcutsOptions {
  onOpenPalette: () => void
  onToggleSidebar: () => void
}

/** Global shortcuts: Cmd/Ctrl+K, "/", "[", and g-then-letter navigation. */
export function useShellShortcuts({ onOpenPalette, onToggleSidebar }: ShellShortcutsOptions): void {
  const [, navigate] = useLocation()
  const pendingGo = useRef<number | null>(null)

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        onOpenPalette()
        return
      }
      if (event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return
      if (document.querySelector('[role="dialog"]')) return

      if (pendingGo.current !== null) {
        window.clearTimeout(pendingGo.current)
        pendingGo.current = null
        const path = GO_SHORTCUTS[event.key.toLowerCase()]
        if (path) {
          event.preventDefault()
          navigate(path)
        }
        return
      }
      if (event.key === '/') {
        event.preventDefault()
        onOpenPalette()
      } else if (event.key === '[') {
        event.preventDefault()
        onToggleSidebar()
      } else if (event.key === 'g') {
        pendingGo.current = window.setTimeout(() => {
          pendingGo.current = null
        }, 1200)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      if (pendingGo.current !== null) window.clearTimeout(pendingGo.current)
    }
  }, [navigate, onOpenPalette, onToggleSidebar])
}

export interface CommandPaletteProps {
  open: boolean
  onClose: () => void
  onToggleSidebar: () => void
}

export function CommandPalette({ open, onClose, onToggleSidebar }: CommandPaletteProps) {
  if (!open || typeof document === 'undefined') return null
  return createPortal(<PalettePanel onClose={onClose} onToggleSidebar={onToggleSidebar} />, document.body)
}

function PalettePanel({ onClose, onToggleSidebar }: Omit<CommandPaletteProps, 'open'>) {
  const { t, i18n } = useTranslation(['console', 'common'])
  const [location, navigate] = useLocation()
  const navItems = useNavItems()
  const theme = useTheme()
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const commands = useMemo<Command[]>(() => {
    const english = i18n.getFixedT('en', 'console')
    const chinese = i18n.getFixedT('zh', 'console')
    const both = (key: string) => `${english(key)} ${chinese(key)}`
    const pages: Command[] = navItems.map((item) => {
      const goKey = GO_KEY_FOR_PATH[item.path]
      return {
        id: `page:${item.path}`,
        label: t(`console:${item.labelKey}`),
        keywords: `${both(item.labelKey)} ${item.path} ${t(`console:nav.group.${item.group}`)}`,
        group: t('console:palette.groupPages'),
        icon: NAV_ICONS[item.icon],
        shortcut: goKey ? ['G', goKey.toUpperCase()] : undefined,
        run: () => {
          navigate(item.path)
        },
      }
    })
    const nextLanguage = currentLanguage() === 'zh' ? 'en' : 'zh'
    const actions: Command[] = [
      {
        id: 'action:theme',
        label:
          theme.resolved === 'dark'
            ? t('console:palette.actionLightTheme')
            : t('console:palette.actionDarkTheme'),
        keywords: `theme dark light ${both('palette.actionLightTheme')} ${both('palette.actionDarkTheme')}`,
        group: t('console:palette.groupActions'),
        icon: theme.resolved === 'dark' ? SunIcon : MoonIcon,
        run: theme.toggle,
      },
      {
        id: 'action:language',
        label: t('console:palette.actionLanguage'),
        keywords: `language english chinese ${both('palette.actionLanguage')}`,
        group: t('console:palette.groupActions'),
        icon: GlobeIcon,
        run: () => {
          setLanguage(nextLanguage)
        },
      },
      {
        id: 'action:sidebar',
        label: t('console:palette.actionSidebar'),
        keywords: `sidebar collapse expand ${both('palette.actionSidebar')}`,
        group: t('console:palette.groupActions'),
        icon: ChevronLeftIcon,
        shortcut: ['['],
        run: onToggleSidebar,
      },
      {
        id: 'action:refresh',
        label: t('console:palette.actionRefresh'),
        keywords: `refresh reload ${both('palette.actionRefresh')}`,
        group: t('console:palette.groupActions'),
        icon: RefreshIcon,
        run: () => {
          void queryClient.invalidateQueries()
        },
      },
    ]
    return [...pages, ...actions]
  }, [i18n, navigate, navItems, onToggleSidebar, queryClient, t, theme])

  const visible = useMemo(() => {
    const q = normalise(query)
    return commands
      .map((command, index) => ({ command, rank: score(command, q), index }))
      .filter((entry) => entry.rank > 0)
      .sort((a, b) => (q ? b.rank - a.rank || a.index - b.index : a.index - b.index))
      .map((entry) => entry.command)
  }, [commands, query])

  const clamped = Math.min(active, Math.max(visible.length - 1, 0))

  useEffect(() => {
    const previous = document.activeElement
    inputRef.current?.focus()
    return () => {
      if (previous instanceof HTMLElement) previous.focus()
    }
  }, [])

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${String(clamped)}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [clamped])

  function runCommand(command: Command | undefined) {
    if (!command) return
    onClose()
    command.run()
  }

  function onKeyDown(event: ReactKeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((clamped + 1) % Math.max(visible.length, 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((clamped - 1 + visible.length) % Math.max(visible.length, 1))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      runCommand(visible[clamped])
    } else if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
    }
  }

  let lastGroup = ''

  return (
    <div
      className={styles.backdrop}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={t('console:palette.title')}
        onKeyDown={onKeyDown}
      >
        <div className={styles.searchRow}>
          <SearchIcon size={16} />
          <input
            ref={inputRef}
            className={styles.input}
            value={query}
            placeholder={t('console:palette.placeholder')}
            aria-label={t('console:palette.placeholder')}
            aria-controls="tk-palette-list"
            aria-activedescendant={visible[clamped] ? `tk-cmd-${String(clamped)}` : undefined}
            role="combobox"
            aria-expanded="true"
            spellCheck={false}
            autoComplete="off"
            onChange={(event) => {
              setQuery(event.target.value)
              setActive(0)
            }}
          />
          <kbd>Esc</kbd>
        </div>

        <div className={styles.list} id="tk-palette-list" role="listbox" ref={listRef}>
          {visible.length === 0 ? (
            <div className={styles.empty}>{t('console:palette.empty')}</div>
          ) : (
            visible.map((command, index) => {
              const Icon = command.icon
              const header = command.group !== lastGroup ? command.group : null
              lastGroup = command.group
              const current = command.id === `page:${location}`
              return (
                <div key={command.id}>
                  {header ? <div className={styles.groupTitle}>{header}</div> : null}
                  <div
                    id={`tk-cmd-${String(index)}`}
                    data-index={index}
                    role="option"
                    aria-selected={index === clamped}
                    className={cn(styles.item, index === clamped && styles.itemActive)}
                    onMouseMove={() => {
                      if (index !== clamped) setActive(index)
                    }}
                    onClick={() => {
                      runCommand(command)
                    }}
                  >
                    <span className={styles.itemIcon}>
                      <Icon size={15} />
                    </span>
                    <span className={styles.itemLabel}>{command.label}</span>
                    {current ? <span className={styles.itemMeta}>{t('console:palette.current')}</span> : null}
                    {command.shortcut ? (
                      <span className={styles.itemKeys}>
                        {command.shortcut.map((key) => (
                          <kbd key={key}>{key}</kbd>
                        ))}
                      </span>
                    ) : null}
                  </div>
                </div>
              )
            })
          )}
        </div>

        <div className={styles.footer}>
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> {t('console:palette.hintMove')}
          </span>
          <span>
            <kbd>↵</kbd> {t('console:palette.hintOpen')}
          </span>
          <span className={styles.footerRight}>
            <kbd>G</kbd> + <kbd>O</kbd>/<kbd>I</kbd>/<kbd>P</kbd>… {t('console:palette.hintGo')}
          </span>
        </div>
      </div>
    </div>
  )
}
